// Custom hook for organization form state and logic
import { useState, useEffect, useCallback } from 'react';
import { IOrganization, ICreateOrganization } from '@/types/admin/organizations';
import { DEFAULT_ORGANIZATION_FORM_STATE, DEFAULT_ORGANIZATION_ERRORS } from '@/constants/admin/organizationFormDefaults';
import { 
  usePostOrganizationMutation, 
  usePutOrganizationMutation,
  useDeleteOrganizationMutation,
} from '@/store/api/admin/organizationsApi';
import { useToast } from '@/contexts/admin/ToastContext';

interface UseOrganizationFormProps {
  organization?: IOrganization | null;
  onSuccess?: () => void;
  onClose?: () => void;
}

interface OrganizationFormData {
  name: string;
  domain: string;
  paymentAccounts: any[];
}

export const useOrganizationForm = ({ 
  organization, 
  onSuccess, 
  onClose 
}: UseOrganizationFormProps) => {
  const { showSuccess, showError } = useToast();
  const isEditMode = !!organization;

  // API mutations
  const [postOrganization, { isLoading: isCreating }] = usePostOrganizationMutation();
  const [putOrganization, { isLoading: isUpdating }] = usePutOrganizationMutation();
  const [deleteOrganization, { isLoading: isDeleting }] = useDeleteOrganizationMutation();
  // Form state
  const [formData, setFormData] = useState<OrganizationFormData>(DEFAULT_ORGANIZATION_FORM_STATE);
  const [errors, setErrors] = useState(DEFAULT_ORGANIZATION_ERRORS);

  // Update form data when organization changes
  useEffect(() => {
    if (isEditMode && organization) {
      setFormData({
        name: organization.name || '',
        domain: organization.domain || '',
        paymentAccounts: organization.paymentAccounts || DEFAULT_ORGANIZATION_FORM_STATE.paymentAccounts,
      });
    }
  }, [isEditMode, organization]);

  // Validation
  const validateForm = useCallback((): boolean => {
    const newErrors = {
      name: '',
      domain: '',
    };

    if (!formData.name.trim()) {
      newErrors.name = 'Organization name is required';
    }

    if (!formData.domain.trim()) {
      newErrors.domain = 'Domain is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.domain)) {
      newErrors.domain = 'Domain must contain only lowercase letters, numbers, and hyphens';
    }

    setErrors(newErrors);
    return !newErrors.name && !newErrors.domain;
  }, [formData]);

  // Reset form
  const resetForm = useCallback(() => {
    setFormData(DEFAULT_ORGANIZATION_FORM_STATE);
    setErrors(DEFAULT_ORGANIZATION_ERRORS);
  }, []);

  // Update form field
  const updateField = useCallback(<K extends keyof OrganizationFormData>(
    field: K,
    value: OrganizationFormData[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Update payment account
  const updatePaymentAccount = useCallback((index: number, accountNumber: string) => {
    const newAccounts = [...formData.paymentAccounts];
    newAccounts[index] = {
      ...newAccounts[index],
      accountNumber: accountNumber || null,
    };
    setFormData(prev => ({ ...prev, paymentAccounts: newAccounts }));
  }, [formData.paymentAccounts]);

  // Create organization
  const createOrganization = useCallback(async () => {
    if (!validateForm()) return;

    try {
      const orgData: ICreateOrganization = {
        name: formData.name.trim(),
        domain: formData.domain.trim(),
        paymentAccounts: formData.paymentAccounts,
      };

      await postOrganization(orgData).unwrap();

      showSuccess(`Organization "${formData.name}" created successfully`);
      onSuccess?.();
      onClose?.();
      resetForm();
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to create organization';
      showError(errorMessage);
    }
  }, [formData, validateForm, postOrganization, showSuccess, showError, onSuccess, onClose, resetForm]);

  // Update organization
  const updateOrganization = useCallback(async () => {
    if (!validateForm() || !organization) return;

    try {
      const updateData: IOrganization = {
        ...organization,
        name: formData.name.trim(),
        domain: formData.domain.trim(),
        paymentAccounts: formData.paymentAccounts,
      };

      await putOrganization(updateData).unwrap();

      showSuccess(`Organization "${formData.name}" updated successfully`);
      onSuccess?.();
      onClose?.();
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to update organization';
      showError(errorMessage);
    }
  }, [formData, validateForm, organization, putOrganization, showSuccess, showError, onSuccess, onClose]);

  // Delete organization
  const removeOrganization = useCallback(async () => {
    if (!organization) return;

    try {
      await deleteOrganization(organization.id).unwrap();
      showSuccess(`Organization "${organization.name}" deleted successfully`);
      onClose?.();
      onSuccess?.();
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to delete organization';
      showError(errorMessage);
    }
  }, [organization, deleteOrganization, showSuccess, showError, onClose, onSuccess]);

  return {
    // State
    formData,
    errors,
    isEditMode,

    // Loading states
    isLoading: isCreating || isUpdating,
    isCreating,
    isUpdating,
    isDeleting,

    // Actions
    updateField,
    updatePaymentAccount,
    validateForm,
    resetForm,
    createOrganization,
    updateOrganization,
    removeOrganization,
  };
};

